import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Toast from '@/components/ui/Toast';
import { daysBetween, shortDate } from '@/lib/format';
import { portalReservations, type PortalReservation } from '@/mocks/portal';

const TODAY = '2026-10-24';

type Filter = 'All' | 'Active' | 'Converted' | 'Expired' | 'Declined';

export default function PortalReservationsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PortalReservation[]>(portalReservations);
  const [filter, setFilter] = useState<Filter>('All');
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [declineTarget, setDeclineTarget] = useState<PortalReservation | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);

  const filters: Filter[] = ['All', 'Active', 'Converted', 'Expired', 'Declined'];

  const visible = useMemo(
    () => rows.filter((r) => filter === 'All' || r.status === filter),
    [rows, filter],
  );

  const confirm = (row: PortalReservation) => {
    setConfirmed((prev) => [...prev, row.no]);
    setToast({ message: t('portal.reservations.confirmedToast', { no: row.no }) });
  };

  const submitDecline = () => {
    if (!declineTarget) return;
    setRows((prev) => prev.map((r) => (r.no === declineTarget.no ? { ...r, status: 'Declined', declineReason: reason } : r)));
    setToast({ message: t('portal.reservations.declinedToast', { no: declineTarget.no }), detail: reason || undefined });
    setDeclineTarget(null);
    setReason('');
  };

  const countdown = (row: PortalReservation) => {
    if (row.status !== 'Active') {
      if (row.status === 'Expired') return t('portal.reservations.expiredOn', { date: shortDate(row.expires) });
      return shortDate(row.expires);
    }
    const days = daysBetween(TODAY, row.expires);
    if (days < 0) return t('portal.reservations.expiredOn', { date: shortDate(row.expires) });
    if (days === 0) return t('portal.reservations.expiresToday');
    return t('portal.reservations.expiresIn', { count: days });
  };

  const urgent = (row: PortalReservation) => row.status === 'Active' && daysBetween(TODAY, row.expires) <= 2;

  const itemText = (row: PortalReservation) => row.lines.map((l) => `${l.sku} × ${l.qty}`).join(' · ');
  const units = (row: PortalReservation) => row.lines.reduce((s, l) => s + l.qty, 0);

  return (
    <div>
      <div className="mb-4 border-b border-background-200 pb-4">
        <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.reservations.title')}</h1>
        <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.reservations.subtitle')}</p>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-[11.5px] font-medium transition-colors ${
              filter === f ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {f === 'All' ? t('portal.reservations.filterAll') : t(`portal.status.${f}`)}
            {f === 'All' && <span className="tnum ml-1 text-foreground-400">{rows.length}</span>}
          </button>
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:block">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[980px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.reservations.colNumber')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.reservations.colSpec')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.reservations.colItems')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.reservations.colQty')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.reservations.colCreated')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.reservations.colExpires')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.reservations.colStatus')}</th>
                <th className="px-4 py-2.5 font-medium">{t('portal.reservations.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.no} className="border-b border-background-200/70 last:border-0 hover:bg-background-100/40">
                  <td className="px-4 py-2.5">
                    <p className="tnum text-[12px] font-semibold text-foreground-950">{row.no}</p>
                    <p className="text-[10px] text-foreground-400">{t('portal.reservations.colCreated')} {shortDate(row.created)}</p>
                  </td>
                  <td className="tnum px-3 py-2.5 text-[11.5px] font-medium text-primary-700">{row.spec}</td>
                  <td className="px-3 py-2.5">
                    <p className="tnum max-w-[230px] truncate text-[11.5px] text-foreground-800">{itemText(row)}</p>
                  </td>
                  <td className="tnum px-3 py-2.5 text-right font-medium text-foreground-900">{units(row)}</td>
                  <td className="tnum px-3 py-2.5 text-[11px] text-foreground-500">{shortDate(row.created)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col gap-1">
                      <span className="tnum text-[11px] text-foreground-700">{shortDate(row.expires)}</span>
                      <span
                        className={`inline-flex w-fit items-center gap-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ${
                          urgent(row)
                            ? 'bg-rose-50 text-rose-700'
                            : row.status === 'Active'
                              ? 'bg-accent-50 text-accent-800'
                              : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        <i className="ri-time-line text-[10px]" />
                        {countdown(row)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <PortalStatus status={row.status} />
                  </td>
                  <td className="px-4 py-2.5">
                    {row.status === 'Active' && !confirmed.includes(row.no) ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => confirm(row)}
                          className="cursor-pointer whitespace-nowrap rounded border border-primary-300 bg-primary-50 px-2 py-1 text-[10.5px] font-semibold text-primary-800 hover:bg-primary-100"
                        >
                          {t('portal.reservations.confirmBtn')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeclineTarget(row);
                            setReason('');
                          }}
                          className="cursor-pointer whitespace-nowrap rounded border border-background-300 bg-background-50 px-2 py-1 text-[10.5px] font-medium text-foreground-600 hover:bg-background-100"
                        >
                          {t('portal.reservations.declineBtn')}
                        </button>
                      </div>
                    ) : confirmed.includes(row.no) ? (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-primary-700">
                        <i className="ri-checkbox-circle-fill text-[12px]" />
                        {t('portal.status.Confirmed')}
                      </span>
                    ) : (
                      <span className="text-[10.5px] text-foreground-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('portal.reservations.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-1 border-t border-background-200 px-4 py-2.5 text-[10.5px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
          <p>{t('portal.reservations.note')}</p>
          <p>{t('portal.reservations.specNote')}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 lg:hidden">
        {visible.map((row) => (
          <div key={row.no} className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="tnum text-[12px] font-semibold text-foreground-950">{row.no}</p>
                <p className="tnum text-[10.5px] font-medium text-primary-700">{row.spec}</p>
              </div>
              <PortalStatus status={row.status} className="!px-1.5 !text-[10px]" />
            </div>
            <p className="tnum mt-2 text-[11.5px] text-foreground-800">{itemText(row)}</p>
            <div className={`mt-2 flex items-center justify-between rounded-md px-2 py-1.5 ${
              urgent(row) ? 'bg-rose-50' : 'bg-background-100'
            }`}>
              <span className="text-[10.5px] text-foreground-500">
                {t('portal.reservations.colExpires')} {shortDate(row.expires)}
              </span>
              <span className={`text-[10.5px] font-semibold ${urgent(row) ? 'text-rose-700' : 'text-foreground-800'}`}>
                {countdown(row)}
              </span>
            </div>
            {row.status === 'Active' && !confirmed.includes(row.no) ? (
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => confirm(row)}
                  className="h-9 flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 text-[12px] font-semibold text-background-50"
                >
                  {t('portal.reservations.confirmBtn')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeclineTarget(row);
                    setReason('');
                  }}
                  className="h-9 flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 text-[12px] font-medium text-foreground-700"
                >
                  {t('portal.reservations.declineBtn')}
                </button>
              </div>
            ) : (
              confirmed.includes(row.no) && (
                <p className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-primary-700">
                  <i className="ri-checkbox-circle-fill text-[12px]" />
                  {t('portal.status.Confirmed')}
                </p>
              )
            )}
          </div>
        ))}
        {visible.length === 0 && (
          <p className="rounded-lg border border-background-200 bg-background-50 px-3 py-8 text-center text-[12px] text-foreground-500">
            {t('portal.reservations.empty')}
          </p>
        )}
      </div>

      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">
              {t('portal.reservations.declineTitle', { no: declineTarget.no })}
            </h2>
            <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('portal.reservations.declineSub')}</p>
            <label className="mt-3 block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">
                {t('portal.reservations.declineReason')}
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 300))}
                rows={3}
                placeholder={t('portal.reservations.declinePlaceholder')}
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-[12px] outline-none focus:border-primary-400"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeclineTarget(null)}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
              >
                {t('portal.common.cancel')}
              </button>
              <button
                type="button"
                onClick={submitDecline}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-rose-600 px-3 text-[12px] font-semibold text-background-50 hover:bg-rose-700"
              >
                {t('portal.reservations.declineSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} onClose={() => setToast(null)} />}
    </div>
  );
}